/**
 * VÉRAULT — Backend Keep-Alive
 * ────────────────────────────────────────────────────────────────
 * Pings `/api/health` every 4 minutes while ANY browser tab is open.
 *
 * Why this exists
 *   The Emergent autoscaler can scale a backend pod down to zero
 *   after a few minutes of inactivity. When a member or advisor
 *   opens the app and *then* tries to save five minutes later, the
 *   first request pays the cold-start cost (5-15s) — which the
 *   axios timeout often kills, surfacing as a "couldn't save" toast.
 *
 *   By having every active tab quietly ping `/api/health` every
 *   four minutes, we keep traffic flowing so the pod stays warm
 *   for as long as a user is browsing. The endpoint is cheap
 *   (a single Mongo ping) and the request is fire-and-forget — if
 *   the user closes the tab, the interval is cleared.
 *
 * Tab Visibility
 *   When the tab goes hidden (background), we slow the ping to a
 *   single shot on next visibility change so we don't waste cycles
 *   on dormant tabs.
 *
 * Production guardrail
 *   If the user's network is offline (navigator.onLine === false)
 *   we skip the ping silently. No console noise, no toasts.
 */
import { useEffect } from "react";

const PING_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

async function pingHealth() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  try {
    // Use the fetch API directly so we don't pull in axios for this
    // tiny request. `keepalive: true` lets the browser finish the
    // ping even if the tab is closing.
    await fetch(`${BACKEND_URL}/api/health`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      keepalive: true,
    });
  } catch {
    // Silent — if the network just blipped, the next interval picks up.
  }
}

export function useBackendKeepAlive() {
  useEffect(() => {
    if (!BACKEND_URL) return undefined;
    let intervalId = null;

    const start = () => {
      if (intervalId !== null) return;
      // Fire one immediately on mount/wake so a freshly-opened tab
      // warms the pod right away.
      pingHealth();
      intervalId = setInterval(pingHealth, PING_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    start();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", start);
      window.addEventListener("offline", stop);
    }

    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("online", start);
        window.removeEventListener("offline", stop);
      }
    };
  }, []);
}

export default useBackendKeepAlive;
